import React from "react";
import { motion } from "framer-motion";
import {
  Ship, ShieldCheck, Globe2, Handshake, BadgeCheck, Wallet,
  Truck, Eye, Wheat, Pill, Leaf, Boxes,
} from "lucide-react";

export const EASE = [0.76, 0, 0.24, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay: i * 0.08 },
  }),
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};

// Reveal-on-scroll wrapper
export const Reveal = ({ children, className = "", delay = 0, as = "div" }) => {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      custom={delay}
      variants={fadeUp}
    >
      {children}
    </M>
  );
};

export const LOGO =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_am-ventures-build/artifacts/7zcetdgh_ChatGPT%20Image%20Jun%2030%2C%202026%2C%2001_33_46%20AM.png";

export const HERO_IMG =
  "https://images.pexels.com/photos/14810111/pexels-photo-14810111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const CATEGORIES = [
  {
    key: "rice",
    name: "Rice",
    icon: Wheat,
    blurb: "Premium Basmati & non-Basmati varieties sourced from certified mills.",
    img: "https://images.unsplash.com/photo-1723879683042-8e72c359ec2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxiYXNtYXRpJTIwcmljZSUyMHNhY2slMjBtYWNyb3xlbnwwfHx8fDE3ODU0MzUzNDd8MA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-2",
  },
  {
    key: "food",
    name: "Food Grade Products",
    icon: Leaf,
    blurb: "Certified, traceable food-grade goods meeting global safety standards.",
    img: "https://images.unsplash.com/photo-1532709031516-391805a39eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxmb29kJTIwaW5ncmVkaWVudHMlMjBwcmVtaXVtJTIwbWFjcm98ZW58MHx8fHwxNzg1NDM1MzQ3fDA&ixlib=rb-4.1.0&q=85",
    span: "",
  },
  {
    key: "pharma",
    name: "Pharmaceuticals",
    icon: Pill,
    blurb: "Compliant pharmaceutical supply with rigorous documentation.",
    img: "https://images.unsplash.com/photo-1666902752583-c2e9b6174927?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxtZWRpY2luZSUyMHBpbGxzJTIwbWFjcm98ZW58MHx8fHwxNzg1NDM1MzUzfDA&ixlib=rb-4.1.0&q=85",
    span: "",
  },
  {
    key: "spices",
    name: "Spices",
    icon: Boxes,
    blurb: "Vibrant, aromatic Indian spices graded for export excellence.",
    img: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwaW5kaWFuJTIwc3BpY2VzJTIwY29sb3JmdWx8ZW58MHx8fHwxNzg1NDM1MzQ3fDA&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-2",
  },
];

export const WHY = [
  { icon: Globe2, title: "Reliable Global Sourcing", text: "Access to vetted origins across continents." },
  { icon: Handshake, title: "Trusted Supplier Network", text: "Long-standing relationships with quality manufacturers." },
  { icon: BadgeCheck, title: "Quality Commitment", text: "Every consignment inspected against strict benchmarks." },
  { icon: Ship, title: "International Trade Expertise", text: "Decades of combined cross-border trade knowledge." },
  { icon: Wallet, title: "Competitive Pricing", text: "Fair, transparent pricing built on efficiency." },
  { icon: Truck, title: "Efficient Logistics Coordination", text: "Seamless freight, customs and delivery handling." },
  { icon: Eye, title: "Transparent Business Practices", text: "Clear communication at every stage of trade." },
  { icon: ShieldCheck, title: "Long-Term Partnerships", text: "We build relationships, not one-off transactions." },
];

export const PROCESS = [
  { n: "01", title: "Requirement Analysis", text: "We understand your specifications, volumes and destination markets." },
  { n: "02", title: "Supplier Identification", text: "We match your needs with trusted, verified manufacturers." },
  { n: "03", title: "Quality Inspection", text: "Independent checks ensure every product meets standards." },
  { n: "04", title: "Documentation", text: "Complete, compliant paperwork for smooth customs clearance." },
  { n: "05", title: "Shipping & Delivery", text: "Coordinated logistics deliver goods reliably, worldwide." },
];

export const FOUNDERS = [
  {
    name: "Parth Ahuja",
    role: "Co-Founder",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_am-ventures-build/artifacts/hk6a8n6g_Generated%20image%201%20%281%29.png",
    bio: "Parth drives AM Ventures' sourcing strategy and supplier relationships, championing quality and integrity in every deal across international markets.",
  },
  {
    name: "Kartik Mundeja",
    role: "Co-Founder",
    img: "https://customer-assets-4nw71qhi.emergentagent.net/job_am-ventures-build/artifacts/5k2a73ar_Generated%20image%201.png",
    bio: "Kartik leads operations and global logistics, ensuring transparent processes and dependable delivery that buyers and suppliers can trust.",
  },
];
